import { Popper } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useCallback } from 'react'
import { Placement } from 'react-overlays/cjs/usePopper'
import { Checkbox } from '../../../../components/inputs/Checkbox'
import { Text } from '../../../../components/typography'
import { SelectOption } from '../../../../model/selectOption'
import { colorPalette } from '../../../../style/colorPalette'
import {
  TextMatchCondition,
  TextMatchMode,
} from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import checkboxInactiveSIcon from '../../../../../assets/checkbox_inactive_s.svg'
import checkboxActiveSIcon from '../../../../../assets/checkbox_active_s.svg'
import checkboxExcludeSIcon from '../../../../../assets/checkbox_exclude_s.svg'

const StyledPopper = styled(Popper)({
  zIndex: 1300,
  background: colorPalette.monotone[0],
  boxShadow:
    'rgb(0 0 0 / 20%) 0px 5px 5px -3px, rgb(0 0 0 / 14%) 0px 8px 10px 1px, rgb(0 0 0 / 12%) 0px 3px 14px 2px',
  borderRadius: '4px',
  padding: '10px',
  minWidth: '445px',
  maxHeight: '300px',
  overflowY: 'auto',
})

const OptionRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
})

const OptionLabel = styled(Text)({
  margin: 'auto 10px',
})

const ExcludeOptionLabel = styled(Text)({
  margin: 'auto 10px',
  textDecoration: 'line-through',
  color: colorPalette.monotone[4],
})

const TriStateCheckboxButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '3px',
  marginRight: '8px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  borderRadius: '4px',
  '&:hover': {
    backgroundColor: colorPalette.monotone[1],
  },
})

type BaseProps = {
  open: boolean
  anchorEl: Element | undefined
  options: SelectOption[]
  offset?: number
  placement?: Placement | undefined
  maxHeight?: string
}

type StandardProps = BaseProps & {
  triState?: false
  value: string[] | undefined
  onChange: (newValue: string[] | undefined) => void
}

type TriStateProps = BaseProps & {
  triState: true
  value: TextMatchCondition[] | undefined
  onChange: (newValue: TextMatchCondition[] | undefined) => void
}

type Props = StandardProps | TriStateProps

const popperModifiers = (offset?: number) => [
  {
    name: 'offset',
    options: { offset: [offset ? offset : 0, 20] },
  },
  {
    name: 'flip',
    enabled: true,
    options: {
      altBoundary: true,
      rootBoundary: 'document',
      padding: 8,
    },
  },
  {
    name: 'preventOverflow',
    enabled: true,
    options: {
      altAxis: true,
      altBoundary: true,
      tether: true,
      rootBoundary: 'document',
      padding: 8,
    },
  },
]

const nextTriState = (
  current: TextMatchMode | undefined
): TextMatchMode | undefined => {
  switch (current) {
    case undefined:
      return 'include'
    case 'include':
      return 'exclude'
    case 'exclude':
      return undefined
  }
}

const triStateIcon: Record<string, string> = {
  include: checkboxActiveSIcon,
  exclude: checkboxExcludeSIcon,
}

export const WbsSearchConditionCheckBoxPopper = (props: Props) => {
  const { open, anchorEl, options, offset, placement, triState } = props

  const onChangeStandard = useCallback(
    (v: any) => {
      if (props.triState) return
      const value = props.value
      const newValue = [...(value || [])]
      if (newValue.includes(v.target.defaultValue)) {
        newValue.splice((value || []).indexOf(v.target.defaultValue), 1)
      } else {
        newValue.push(v.target.defaultValue)
      }
      props.onChange(newValue)
    },
    [props.triState ? undefined : props.value, props.onChange]
  )

  const onChangeTriState = useCallback(
    (optionValue: string) => {
      if (!props.triState) return
      const value = props.value
      const existing = value?.find(c => c.value === optionValue)
      const currentMode = existing?.mode
      const next = nextTriState(currentMode)

      if (next === undefined) {
        const filtered = (value ?? []).filter(c => c.value !== optionValue)
        props.onChange(filtered.length > 0 ? filtered : undefined)
      } else if (existing) {
        const updated = (value ?? []).map(c =>
          c.value === optionValue ? { ...c, mode: next } : c
        )
        props.onChange(updated)
      } else {
        props.onChange([...(value ?? []), { value: optionValue, mode: next }])
      }
    },
    [props.triState ? props.value : undefined, props.onChange]
  )

  const getTriStateMode = (optionValue: string): TextMatchMode | undefined => {
    if (!props.triState) return undefined
    return props.value?.find(c => c.value === optionValue)?.mode
  }

  return (
    <StyledPopper
      open={open}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={popperModifiers(offset)}
      disablePortal={true}
    >
      {options.map(option =>
        triState ? (
          <TriStateOptionRow
            key={option.value}
            option={option}
            mode={getTriStateMode(option.value)}
            onClick={onChangeTriState}
          />
        ) : (
          <StandardOptionRow
            key={option.value}
            option={option}
            checked={
              !props.triState &&
              (props.value || []).some(v => v === option.value)
            }
            onChange={onChangeStandard}
          />
        )
      )}
    </StyledPopper>
  )
}

const StandardOptionRow = ({
  option,
  checked,
  onChange,
}: {
  option: SelectOption
  checked: boolean
  onChange: (v: any) => void
}) => (
  <OptionRow>
    <Checkbox
      sx={{ marginRight: '8px' }}
      value={option.value}
      checked={checked}
      onChange={onChange}
    />
    <img src={option.iconUrl} />
    <OptionLabel color={colorPalette.monotone[4]}>{option.name}</OptionLabel>
  </OptionRow>
)

const TriStateOptionRow = ({
  option,
  mode,
  onClick,
}: {
  option: SelectOption
  mode: TextMatchMode | undefined
  onClick: (optionValue: string) => void
}) => {
  const isExclude = mode === 'exclude'
  const iconSrc = mode ? triStateIcon[mode] : checkboxInactiveSIcon
  const Label = isExclude ? ExcludeOptionLabel : OptionLabel

  return (
    <OptionRow>
      <TriStateCheckboxButton onClick={() => onClick(option.value)}>
        <img src={iconSrc} width={18} height={18} />
      </TriStateCheckboxButton>
      <img src={option.iconUrl} />
      <Label color={colorPalette.monotone[4]}>{option.name}</Label>
    </OptionRow>
  )
}
